import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { SharedUiModule, TemplateDrawerComponent } from '@pf/shared-ui';
import { NZ_DRAWER_DATA, NzDrawerRef } from 'ng-zorro-antd/drawer';
import { CpwActionCardComponent } from './cpw-action-card.component';

export interface CpwCartEmptyDrawerData {
    barcodes: string[];
}

/**
 * Shared "cart is empty" alert drawer used when a packing/put-away task
 * finishes and leaves an empty cart. Combines the put-away success alert card
 * with the packing attention drawer + an explicit confirm. Open it via
 * {@link CpwCartEmptyDrawerService} so both screens behave identically.
 */
@Component({
    selector: 'cpw-cart-empty-drawer',
    standalone: true,
    imports: [SharedUiModule, CpwActionCardComponent],
    template: `
        <div class="cart-empty-drawer">
            <cpw-action-card
                actionType="success"
                [title]="title"
                [description]="message"></cpw-action-card>
            <pf-button buttonType="submit" block (click)="drawerRef.close()">
                Got it
            </pf-button>
        </div>
    `,
    styles: [
        `
            .cart-empty-drawer {
                display: flex;
                flex-direction: column;
                gap: var(--pf-section-gap);
                padding: var(--pf-gap) 0 var(--pf-section-gap);
            }
        `,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CpwCartEmptyDrawerComponent
    implements TemplateDrawerComponent<CpwCartEmptyDrawerData>
{
    readonly data: CpwCartEmptyDrawerData = inject(NZ_DRAWER_DATA);
    drawerRef = inject(NzDrawerRef);

    readonly title: string;
    readonly message: string;

    constructor() {
        const barcodes = (this.data.barcodes ?? []).filter(Boolean);
        const plural = barcodes.length > 1;
        const list = barcodes.join(', ');
        this.title = plural
            ? `Carts ${list} are empty`
            : `Cart ${list} is empty`;
        this.message = plural
            ? 'Please return all empty carts to the available carts area in the warehouse.'
            : 'Please return the empty cart to the available carts area in the warehouse.';
    }
}
